import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MdArrowBack, MdCheckCircle, MdWarning } from 'react-icons/md';
import attendanceAPI from '../../api/attendanceAPI';
import { useAttendanceAuth } from '../../context/AttendanceAuthContext';
import {
  dateToBookingString,
  formatBookingRange,
  fromDateTimeLocal,
  hasCameraMediaRole,
  normalizeCameraFaculties,
  parseBookingDate,
  toDateTimeLocal,
} from '../../utils/cameraBookingUtils';

const EMPTY_FORM = {
  start: '',
  end: '',
  purpose: '',
  location: '',
  notes: '',
  faculty_id: '',
  requested_by_id: '',
};

/**
 * Camera request form.
 * Faculty admins request the camera for their own faculty and wait for media
 * approval. Media users book on behalf of any faculty (optionally one of its
 * admins), and the booking is approved straight away.
 * Availability is checked while the times change; an approved overlap blocks submit.
 */
function CameraRequestForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, getToken } = useAttendanceAuth();

  const isMediaBooker = hasCameraMediaRole(user);

  // Faculty admins see their own faculties from login; media users load every faculty.
  const ownFaculties = user?.faculties?.length
    ? user.faculties
    : user?.faculty
    ? [user.faculty]
    : [];
  const [allFaculties, setAllFaculties] = useState([]);
  const [optionsError, setOptionsError] = useState('');

  const facultyChoices = isMediaBooker ? allFaculties : ownFaculties;
  const showFacultyPicker = isMediaBooker || ownFaculties.length > 1;

  const prefill = location.state || {};
  const [form, setForm] = useState(() => ({
    ...EMPTY_FORM,
    start: toDateTimeLocal(prefill.start || ''),
    end: toDateTimeLocal(prefill.end || ''),
    // Media users start with no faculty, which makes the booking a media reservation.
    faculty_id: isMediaBooker ? '' : String(user?.faculty?.id || ownFaculties[0]?.id || ''),
  }));

  const [availability, setAvailability] = useState(null);
  const [checking, setChecking] = useState(false);
  const [timeError, setTimeError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [overlapWarning, setOverlapWarning] = useState(false);

  const apiStart = fromDateTimeLocal(form.start);
  const apiEnd = fromDateTimeLocal(form.end);

  // Media users: load every faculty and its admins for the dropdowns.
  useEffect(() => {
    if (!isMediaBooker) return undefined;
    let cancelled = false;
    attendanceAPI
      .getCameraOptions(getToken())
      .then((result) => {
        if (cancelled) return;
        setAllFaculties(normalizeCameraFaculties(result));
      })
      .catch((err) => {
        if (!cancelled) setOptionsError(err.message || 'Could not load faculties.');
      });
    return () => {
      cancelled = true;
    };
    // getToken is stable for the session; load the options once for media users.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMediaBooker]);

  // Validate the times locally first; the server still has the final say.
  useEffect(() => {
    setAvailability(null);
    setTimeError('');
    if (!apiStart || !apiEnd) return undefined;

    if (!parseBookingDate(apiStart) || !parseBookingDate(apiEnd)) {
      setTimeError('Enter a valid start and end time.');
      return undefined;
    }
    if (apiEnd <= apiStart) {
      setTimeError('End time must be after the start time.');
      return undefined;
    }
    if (apiStart < dateToBookingString(new Date())) {
      setTimeError('Start time is in the past.');
      return undefined;
    }

    let cancelled = false;
    setChecking(true);
    const timer = setTimeout(async () => {
      try {
        const result = await attendanceAPI.checkCameraAvailability(
          { start: apiStart, end: apiEnd },
          getToken()
        );
        if (!cancelled) setAvailability(result);
      } catch (err) {
        if (!cancelled) setTimeError(err.message || 'Could not check availability.');
      } finally {
        if (!cancelled) setChecking(false);
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // getToken is stable for the session; only the time window drives re-checks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiStart, apiEnd]);

  const updateField = (field) => (event) => {
    setForm((current) => ({ ...current, [field]: event.target.value }));
    setSubmitError('');
    setSuccessMessage('');
    setOverlapWarning(false);
  };

  // Changing the faculty clears the admin choice, which belongs to the old faculty.
  const handleFacultyChange = (event) => {
    setForm((current) => ({
      ...current,
      faculty_id: event.target.value,
      requested_by_id: '',
    }));
    setSubmitError('');
    setSuccessMessage('');
  };

  const selectedFaculty = allFaculties.find((faculty) => String(faculty.id) === String(form.faculty_id));
  // Only media users pick an admin; faculty admins always book for their own faculty.
  const selectedAdmins = isMediaBooker ? selectedFaculty?.admins || [] : [];

  const hasApprovedConflict = availability?.available === false;
  const canSubmit =
    !submitting &&
    !checking &&
    !timeError &&
    availability !== null &&
    !hasApprovedConflict &&
    form.purpose.trim() !== '';

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setSubmitError('');
    setSuccessMessage('');
    try {
      const payload = {
        start: apiStart,
        end: apiEnd,
        purpose: form.purpose.trim(),
        location: form.location.trim(),
        notes: form.notes.trim(),
      };
      if (isMediaBooker) {
        // Media reservations need only times and a purpose. A faculty (and, within
        // it, an admin) is optional and makes the booking on that faculty's behalf.
        if (form.faculty_id) payload.faculty_id = Number(form.faculty_id);
        if (form.faculty_id && form.requested_by_id) {
          payload.requested_by_id = Number(form.requested_by_id);
        }
      } else if (showFacultyPicker && form.faculty_id) {
        payload.faculty_id = Number(form.faculty_id);
      }

      const result = await attendanceAPI.createCameraBooking(payload, getToken());
      setSuccessMessage(
        result.message || (isMediaBooker ? 'Camera booked and approved' : 'Waiting for media approval')
      );
      setOverlapWarning(!isMediaBooker && Boolean(result.overlaps_approved));
      setForm((current) => ({
        ...EMPTY_FORM,
        faculty_id: current.faculty_id,
      }));
      setAvailability(null);
    } catch (err) {
      setSubmitError(err.message || 'Could not send the request.');
    } finally {
      setSubmitting(false);
    }
  };

  const pendingCount = availability?.pending_requests || 0;
  const title = isMediaBooker ? 'Book Camera for a Faculty' : 'Request Camera';
  const subtitle = isMediaBooker
    ? 'Reservations are approved straight away and cannot overlap an approved booking. Choose a faculty only when booking on its behalf.'
    : 'Requests are sent to the media team for approval.';

  return (
    <div className="max-w-3xl space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-800">{title}</h2>
          <p className="text-sm text-gray-500">{subtitle}</p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/attendance/camera/calendar')}
          className="flex items-center gap-1 px-3 py-2 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50"
        >
          <MdArrowBack /> Back to calendar
        </button>
      </div>

      {optionsError && (
        <div className="p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">{optionsError}</div>
      )}

      {successMessage && (
        <div className="p-4 rounded-md bg-green-50 border border-green-200 text-green-800">
          <div className="flex items-center gap-2 font-medium">
            <MdCheckCircle /> {successMessage}
          </div>
          <button
            type="button"
            onClick={() => navigate(isMediaBooker ? '/attendance/camera/calendar' : '/attendance/camera/my-requests')}
            className="mt-2 text-sm underline"
          >
            {isMediaBooker ? 'View calendar' : 'View my requests'}
          </button>
          {overlapWarning && (
            <p className="mt-2 text-sm text-amber-800">
              This time overlaps an approved booking, so the media team will need to reject it.
            </p>
          )}
        </div>
      )}

      {submitError && (
        <div className="p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700">{submitError}</div>
      )}

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow border border-gray-200 p-4 sm:p-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Start</span>
            <input
              type="datetime-local"
              value={form.start}
              onChange={updateField('start')}
              required
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-gray-700">End</span>
            <input
              type="datetime-local"
              value={form.end}
              onChange={updateField('end')}
              required
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </label>
        </div>

        {/* Availability */}
        {timeError && (
          <div className="p-3 rounded-md bg-red-50 text-sm text-red-700">{timeError}</div>
        )}
        {checking && <p className="text-sm text-gray-500">Checking availability…</p>}
        {availability && !timeError && (
          <div className="space-y-2">
            {hasApprovedConflict ? (
              <div className="p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-800">
                <p className="font-medium flex items-center gap-2">
                  <MdWarning /> The camera is already booked for this time.
                </p>
                <ul className="mt-2 list-disc list-inside">
                  {(availability.conflicts || []).map((conflict) => (
                    <li key={conflict.id}>{formatBookingRange(conflict.start, conflict.end)} · Booked</li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="p-3 rounded-md bg-green-50 border border-green-200 text-sm text-green-800">
                The camera is free for this time.
              </div>
            )}
            {!isMediaBooker && pendingCount > 0 && (
              <p className="text-sm text-amber-700">
                {pendingCount} other {pendingCount === 1 ? 'request is' : 'requests are'} pending for this time.
              </p>
            )}
          </div>
        )}

        {showFacultyPicker && (
          <label className="block">
            <span className="text-sm font-medium text-gray-700">
              Faculty{isMediaBooker ? ' (optional)' : ''}
            </span>
            <select
              value={form.faculty_id}
              onChange={handleFacultyChange}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              {isMediaBooker && <option value="">No faculty (media reservation)</option>}
              {facultyChoices.map((faculty) => (
                <option key={faculty.id} value={faculty.id}>
                  {faculty.name || faculty.code}
                </option>
              ))}
            </select>
          </label>
        )}

        {isMediaBooker && selectedAdmins.length > 0 && (
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Booked for (optional)</span>
            <select
              value={form.requested_by_id}
              onChange={updateField('requested_by_id')}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="">The faculty (no specific admin)</option>
              {selectedAdmins.map((admin) => (
                <option key={admin.id} value={admin.id}>
                  {admin.name}
                </option>
              ))}
            </select>
          </label>
        )}

        <label className="block">
          <span className="text-sm font-medium text-gray-700">Purpose *</span>
          <input
            type="text"
            value={form.purpose}
            onChange={updateField('purpose')}
            required
            maxLength={255}
            placeholder="e.g. Graduation photos"
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-gray-700">Location</span>
          <input
            type="text"
            value={form.location}
            onChange={updateField('location')}
            placeholder="e.g. Main hall"
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-gray-700">Notes</span>
          <textarea
            value={form.notes}
            onChange={updateField('notes')}
            rows={3}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
          />
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={() => setForm({ ...EMPTY_FORM, faculty_id: form.faculty_id })}
            className="px-4 py-2 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50"
          >
            Clear
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="px-4 py-2 text-sm font-medium rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? 'Saving…' : isMediaBooker ? 'Book camera' : 'Submit request'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CameraRequestForm;
